/* WHAT WOULD CHANGE HOW YOU SAY HELLO.
 *
 * The register used to answer one question: who is attending. The coach standing in
 * the room needs a second one answered: who should I pay attention to today. This is
 * the only moment in the product where staff are physically in front of these people,
 * so it is the highest-value place to say the thing that changes the greeting: "she
 * has been away 38 days", "this is his second class ever", "nobody has spoken to her
 * in six weeks".
 *
 * IT IS A TOUCHPOINT, NOT A DASHBOARD. Every line is DERIVED from what the record
 * already holds. Nothing here is stored: no flags, no invented numbers. riskModel() is
 * the only thing that scores anybody and this does not second-guess it.
 *
 * CARE TRACK AND HOLD ARE THE RULE THAT MATTERS (SITE-RULES 1). A suppressed member is
 * a person in the room like anybody else. They are never scored, never carry a "needs
 * attention" tag, never get a chase action on their row, and never appear in the
 * counts across the top. They keep the plain facts about being in the room with the
 * colour taken out. That is a safety feature, not a style choice.
 *
 * THE THRESHOLDS LIVE HERE, all four of them, so the register, the bar across the top
 * and the copy cannot drift apart. One number, one meaning, one place to argue about
 * it.
 */
import { daysSince } from './time.js';
import { riskModel } from './risk.js';
/* The same 90 days the lifecycle uses. Inside it a member runs the member track and
   is not scored (SITE-RULES 3b), and two definitions of "new" is how the register
   and the roster end up disagreeing about the same person. */
import { ONBOARDING_DAYS } from './lifecycle.js';
/** Days away before the absence is worth naming out loud. */
export const CTX_GAP = 14;
/** The first three classes: where saying somebody's name decides things. */
export const CTX_EARLY = 3;
/** Days with nobody speaking to them before it is a signal. */
export const CTX_QUIET = 30;
/**
 * Visits in seven days that count as a rhythm worth naming. FOUR, not three: at three
 * the chip landed on half the register, and a line that is true of half the room is
 * furniture. An unlimited member training three times a week is the baseline, not the
 * story.
 */
export const CTX_WEEK = 4;
const ORD = ['th', 'st', 'nd', 'rd'];
function ordinal(n) {
    const v = n % 100;
    return n + (ORD[(v - 20) % 10] ?? ORD[v] ?? ORD[0]);
}
/** A stored date, read the way a person says it. Kept local so the model owes the app
 *  nothing; this is the only formatting the domain does, and only inside a sentence. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function say(d) {
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return (y && m && day) ? `${MONTHS[m - 1]} ${day}` : d;
}
/**
 * The handful of things worth saying about one person on one day.
 *
 * The ordinals are counted from the visits BEFORE the selected day, so a label does
 * not rewrite itself under the coach's hand the moment they tick the box. Only the
 * tense changes.
 */
export function registerContext(m, f, today, o = {}) {
    const suppressed = !!m.suppression;
    /* Suppressed keeps the fact and loses the colour. Nothing on their row is allowed
       to read as a flag. */
    const tone = (t) => (suppressed ? 'flat' : t);
    const items = [];
    // 1. How far into their membership this class is.
    if (f.nth <= CTX_EARLY) {
        items.push({
            key: 'early', tone: tone('good'),
            text: f.nth === 1 ? 'First class' : ordinal(f.nth) + ' class',
            why: f.nth === 1
                ? 'Nothing on record before this one. Introduce them to somebody by name.'
                : `Still new: ${ordinal(f.nth)} class on record. Use their name.`,
        });
    }
    // 2. The gap. The single most useful thing to know before you say hello.
    if (f.gap != null && f.gap >= CTX_GAP) {
        items.push({
            key: 'gap', tone: tone('warn'),
            text: (f.inRoom ? 'Returning after ' : 'Away ') + f.gap + ' days',
            why: 'Ask what changed, then stop talking.',
        });
    }
    // 3. A rhythm worth naming. Consistency is the thing nobody ever gets told.
    if (f.week >= CTX_WEEK) {
        items.push({
            key: 'rhythm', tone: tone('good'),
            text: f.inRoom ? ordinal(f.week) + ' visit this week' : f.week + ' visits this week',
            why: `${f.week} visits in the seven days to ${say(today)}. Worth saying out loud.`,
        });
    }
    /* 4. Needs attention. riskModel is the ONLY scorer, and it is never called for a
          suppressed member or one inside their first 90 days: those two groups are read
          as people, not as a number. */
    const joined = daysSince(m.joinedAt, today);
    const onboarding = joined != null && joined <= ONBOARDING_DAYS;
    if (!suppressed && !onboarding && m.status === 'active') {
        const r = m.risk ?? riskModel(m, { today, ...(o.model ? { model: o.model } : {}) });
        if (r.tier === 'Red') {
            items.push({
                key: 'attention', tone: 'risk', text: 'Needs attention',
                why: r.why.join(' · ') || r.guidance,
            });
        }
    }
    /* 5. Nobody has spoken to them. The line exists to PROMPT A TOUCH, so it is not
          drawn on a suppressed member's row: that would be a chase by another name. */
    if (!suppressed) {
        const quiet = f.lastTouch == null ? null : (daysSince(f.lastTouch, today) ?? 0);
        if (quiet == null) {
            items.push({
                key: 'quiet', tone: 'warn', text: 'No conversation on record',
                why: 'Nobody on staff has logged a word with them.',
            });
        }
        else if (quiet >= CTX_QUIET) {
            items.push({
                key: 'quiet', tone: 'warn',
                text: `Nobody has spoken to them in ${quiet} days`,
                why: `Last recorded contact ${say(f.lastTouch)}.`,
            });
        }
        else if (quiet <= 0 && today === f.lastTouch) {
            items.push({
                key: 'spoken', tone: 'flat', text: 'Spoken to today',
                why: 'Somebody on staff has already logged a conversation today.',
            });
        }
    }
    return items;
}
export const CONTEXT_GROUPS = [
    { key: 'early', label: 'First three classes', empty: 'Nobody in their first three classes.' },
    { key: 'gap', label: 'Returning after a gap', empty: 'Nobody back after a long gap.' },
    { key: 'attention', label: 'Needs attention', empty: 'Nobody here needs chasing.' },
];
/** Which of the three groups this person falls into, from the items already derived.
 *  One set of predicates, so a name in a count across the top is always a name you can
 *  find on a register below. */
export function groupsFor(items, suppressed) {
    const out = new Set();
    if (suppressed)
        return out;
    for (const i of items) {
        if (i.key === 'early')
            out.add('early');
        else if (i.key === 'gap')
            out.add('gap');
        else if (i.key === 'attention')
            out.add('attention');
    }
    return out;
}
